import type { AnyObject } from 'final-form';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import CrmZone from '@swf/components/CrmZone/CrmZone';
import createForm from '@swf/components/Forms/Form';
import { Validation } from '@swf/components/Inputs/Input';
import Password from '@swf/components/Inputs/Password';
import StarsDropDown from '@swf/components/Inputs/StarsDropDown';
import Text from '@swf/components/Inputs/Text';
import Button from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { CRMZone, Country, FeatureFlag, Site } from '@swf/core/enums';
import { ErrorCode } from '@swf/core/enums/errorCode';
import { StatusCode } from '@swf/core/enums/statusCode';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { Dispatch } from '@swf/core/store';
import type { Country as Countries } from '@swf/core/types/i18n';
import {
  checkUserPassword,
  email,
  getCheckUserId,
  getValidatorsForLicense,
  lengthRange,
  minLength,
  mustNotContainField,
  mustStartWithLetter,
  noWhiteStartOrEnd,
  required
} from '@swf/core/utils/validators';
import countriesList from '@swf/generated/countries.json';
import states from '@swf/generated/states.json';

import { RegistrationStyles } from '../../StarsAuth';
import AccountSecurityInfo from '../AccountSecurityInfo/AccountSecurityInfo';
import ErrorMessage from '../ErrorMessage/ErrorMessage';

import {
  TXTACL_Please_Select,
  TXTCLI_A_State_Province_is_required_f_ELL,
  TXTCLI_Continue,
  TXTCLI_Country_of_Residence,
  TXTCLI_Email_address,
  TXTCLI_Must_be_at_least_N0_characters_ELL,
  TXTCLI_Password,
  TXTCLI_Please_select_your_country_of__ELL,
  TXTCLI_State___Province,
  TXTCLI_User_ID,
  TXTWCL_Already_have_a_Stars_IDX_login_ELL,
  TXTWCL_Choose_a_secure_password,
  TXTWCL_Choose_your_login___identity,
  TXTWCL_Enter_a_valid_email_address,
  TXTWCL_For_privacy_and_security_reaso_ELL,
  TXTWCL_Must_be_between_N0_and_N1_char_ELL,
  TXTWCL_Must_not_contain_your_Stars_ID,
  TXTWCL_Must_start_with_a_letter_XA_ZX_ELL,
  TXTWCL_Your_Stars_ID_is_both_your_acc_ELL
} from './RegistrationForm.messages.json';

enum CountryWithStateSelection {
  Argentina = 'AR',
  Russia = 'RU',
  Ukraine = 'UA'
}
const restrictedCountries = ['NL'];

const getInitialCountry = (storeCountry: string): string =>
  !restrictedCountries.includes(storeCountry) &&
  // TODO: import json and provide types before using the variable
  countriesList.some(({ value }: AnyObject) => value === storeCountry)
    ? storeCountry
    : '';

const getCountryName = (countryCode: string): string =>
  // TODO: import json and provide types before using the variable
  (countriesList.find(({ value }: AnyObject) => value === countryCode) as Countries).text;

interface FormState {
  country: string;
  state?: string;
  email: string;
  userId: string;
  password: string;
}

const Form = createForm<FormState>();

const RegistrationForm: React.FC = () => {
  const options = useSelector(select.cms.getRequestOptions);
  const { errorCode, statusCode } = useSelector(select.auth.getResponse);
  const checkUserId = getCheckUserId(options);

  const { formatMessage } = useIntl();
  const siteId = useSelector(select.cms.getSiteId);
  const isSiteUS = useSelector(select.cms.isSiteUS);
  const country = useSelector(select.country.getCountry);

  const {
    crm: { fetchZone }
  } = useDispatch<Dispatch>();

  useEffect(() => {
    fetchZone({ zoneId: CRMZone.SWC_TRANSITION_TO_WC2 });
  }, [fetchZone]);

  const { push } = useHistory();
  const { state } = useLocation<object>();

  const showState = useFeature(FeatureFlag.SignUpShowState);
  const disableBuenosAires = useFeature(FeatureFlag.DisableBuenosAiresFeatures);
  const registrationNotAllowed =
    statusCode === StatusCode.REGISTRATION_BLOCKED_BY_GEO_IP ||
    errorCode === ErrorCode.BLOCKED_BY_GEO_IP;

  const initialCountry = useMemo(() => getInitialCountry(country as string), [country]);

  const showStatesDropDown = (selectedCountry: CountryWithStateSelection): boolean =>
    ([CountryWithStateSelection.Argentina].includes(selectedCountry) && !disableBuenosAires) ||
    ([CountryWithStateSelection.Russia, CountryWithStateSelection.Ukraine].includes(
      selectedCountry
    ) &&
      showState);

  const handleSubmit = (values: FormState) =>
    push(
      '/auth/registration/eula',
      isSiteUS ? { ...values, country: Country.UnitedStates } : values
    );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={{ country: initialCountry, ...state }}
      keepDirtyOnReinitialize
      subscription={{ values: true, invalid: true, validating: true, submitting: true }}
      render={({ form }) => {
        const {
          values: { country: selectedCountry },
          validating,
          submitting
        } = form.getState();
        const countryFieldState = form.getFieldState('country');

        return (
          <RegistrationStyles.Consumer>
            {(styles) => (
              <>
                <ErrorMessage />
                <div className={styles.crm}>
                  <CrmZone zoneId="RAMCREATEACCOUNT" componentType="Single" dynamicHeight />
                </div>
                {!isSiteUS && (
                  <StarsDropDown
                    id="country"
                    options={countriesList}
                    validators={[required(null)]}
                    label={TXTCLI_Country_of_Residence}
                    placeholder={formatMessage(TXTCLI_Please_select_your_country_of__ELL)}
                  />
                )}
                {showStatesDropDown(selectedCountry as CountryWithStateSelection) && (
                  <StarsDropDown
                    id="state"
                    caption={
                      countryFieldState?.modified && (
                        <Translation
                          values={{ 0: getCountryName(selectedCountry) }}
                          {...TXTCLI_A_State_Province_is_required_f_ELL}
                        />
                      )
                    }
                    validators={[required(null)]}
                    options={states[selectedCountry]}
                    label={TXTCLI_State___Province}
                    placeholder={formatMessage(TXTACL_Please_Select)}
                  />
                )}
                <Text
                  autoFocus
                  id="email"
                  autoComplete="nope"
                  validators={[required(null), email]}
                  label={TXTCLI_Email_address}
                  placeholder={formatMessage(TXTWCL_Enter_a_valid_email_address)}
                />
                <Text
                  id="userId"
                  maxLength={15}
                  autoComplete="nope"
                  validationVariant={Validation.List}
                  label={TXTCLI_User_ID}
                  tooltipIconClassName={styles.tooltipIcon}
                  tooltipClassName={styles.tooltip}
                  placeholder={formatMessage(TXTWCL_Choose_your_login___identity)}
                  validators={[
                    minLength(4, {
                      ...TXTCLI_Must_be_at_least_N0_characters_ELL,
                      values: { 0: '4' }
                    }),
                    noWhiteStartOrEnd,
                    checkUserId,
                    ...getValidatorsForLicense(siteId, 'userId')
                  ]}
                  tooltip={[
                    TXTWCL_Your_Stars_ID_is_both_your_acc_ELL,
                    TXTWCL_For_privacy_and_security_reaso_ELL
                  ]}
                />
                <Password
                  id="password"
                  maxLength={20}
                  autoComplete="new-password"
                  validationVariant={Validation.List}
                  label={TXTCLI_Password}
                  placeholder={formatMessage(TXTWCL_Choose_a_secure_password)}
                  validators={[
                    lengthRange(8, 20, {
                      ...TXTWCL_Must_be_between_N0_and_N1_char_ELL,
                      values: { 0: '8', 1: '20' }
                    }),
                    mustNotContainField('userId', TXTWCL_Must_not_contain_your_Stars_ID),
                    mustStartWithLetter(TXTWCL_Must_start_with_a_letter_XA_ZX_ELL),
                    ...getValidatorsForLicense(siteId, 'password'),
                    checkUserPassword
                  ]}
                />
                {[Site.USPA, Site.USMI].includes(siteId) && <AccountSecurityInfo />}
                <ButtonGroup>
                  <Button
                    type="submit"
                    loading={submitting}
                    disabled={validating || registrationNotAllowed}
                    translation={TXTCLI_Continue}
                  />
                  <Button
                    variant="text"
                    href="/auth/login"
                    translation={TXTWCL_Already_have_a_Stars_IDX_login_ELL}
                  />
                </ButtonGroup>
              </>
            )}
          </RegistrationStyles.Consumer>
        );
      }}
    />
  );
};

export default RegistrationForm;
